// Der KI-Assistent.
//
// Drei Wege hinein, ein Weg hinaus:
//   schreiben    — Frage tippen
//   sprechen     — Mikrofon halten, reden, loslassen
//   fotografieren— Lieferschein oder Rechnung ablichten oder hochladen
//
// Alles landet im selben Gespräch. Was der Assistent verändern soll, kommt als
// Karte mit Knopf zurück: erst der Klick bucht. Das gilt für die Vorschläge aus
// dem Gespräch genauso wie für den gescannten Beleg, der die ausführliche
// Prüfansicht öffnet.
import { useCallback, useEffect, useRef, useState } from 'react';
import { api, ApiFehler } from '../../../gemeinsam/lib/api';
import { useAuth } from '../../../gemeinsam/lib/AuthContext';
import { useSprache } from '../../../gemeinsam/lib/i18n';
import { dateiHochladen, groesseLesbar, type Anhang } from '../../firma/lib/dateien';
import { diktatMoeglich, diktatStarten, vorlesen, vorlesenMoeglich, vorlesenStoppen } from '../../../gemeinsam/lib/sprache';
import { Abzeichen, Hinweis, Karte, Knopf, Spinner, TarifSperre } from '../../../gemeinsam/komponenten/Basis';
import Icon from '../../../gemeinsam/komponenten/Icon';
import BelegPruefung, { type ScanVorgang } from '../../belege/komponenten/BelegPruefung';

type Vorschlag = {
  id: string;
  typ: 'bestand' | 'artikel';
  titel: string;
  beschreibung: string;
  daten: Record<string, unknown>;
};

type Zug = {
  rolle: 'ich' | 'assistent';
  text: string;
  zeit?: string | null;
  vorschlaege?: Vorschlag[];
  /** Lokal gesetzt, sobald ein Vorschlag ausgeführt oder verworfen wurde. */
  erledigt?: Record<string, 'gebucht' | 'verworfen'>;
};

type Stand = {
  aktiv: boolean;
  testmodus: boolean;
  scans: { verbraucht: number; grenze: number };
  fragen: { verbraucht: number; grenze: number };
};

export default function Assistent() {
  const { t, sprache } = useSprache();
  const { flags } = useAuth();

  const [zuege, setZuege] = useState<Zug[]>([]);
  const [stand, setStand] = useState<Stand | null>(null);
  const [eingabe, setEingabe] = useState('');
  const [zwischenstand, setZwischenstand] = useState('');
  const [denkt, setDenkt] = useState(false);
  const [fehler, setFehler] = useState<string | null>(null);
  const [laedt, setLaedt] = useState(true);

  const [anhaenge, setAnhaenge] = useState<Anhang[]>([]);
  const [laedtHoch, setLaedtHoch] = useState(false);
  const [scannt, setScannt] = useState(false);
  const [vorgang, setVorgang] = useState<ScanVorgang | null>(null);
  const [artikel, setArtikel] = useState<{ id: string; name: string; artikelnummer?: string; bestand?: number }[]>([]);
  const [lieferanten, setLieferanten] = useState<{ id: string; name: string }[]>([]);

  const [hoert, setHoert] = useState(false);
  const [liest, setLiest] = useState(false);
  const diktatStoppen = useRef<(() => void) | null>(null);
  const endeRef = useRef<HTMLDivElement>(null);
  const dateiFeld = useRef<HTMLInputElement>(null);
  const kameraFeld = useRef<HTMLInputElement>(null);

  const kannSprechen = diktatMoeglich();
  const kannVorlesen = vorlesenMoeglich();

  // --- Laden ---------------------------------------------------------------
  const laden = useCallback(async () => {
    try {
      const [s, v] = await Promise.all([
        api<Stand>('/ki/status'),
        api<{ nachrichten: Zug[] }>('/ki/chat').catch(() => ({ nachrichten: [] as Zug[] })),
      ]);
      setStand(s);
      setZuege(v.nachrichten);
    } catch (e) {
      if (!(e instanceof ApiFehler && e.istTarifgrenze)) {
        setFehler(e instanceof ApiFehler ? e.message : 'Der Assistent ist nicht erreichbar.');
      }
    } finally { setLaedt(false); }
  }, []);

  useEffect(() => { void laden(); }, [laden]);

  // Stammdaten für die Prüfansicht — erst wenn wirklich gescannt wird.
  const stammdatenLaden = useCallback(async () => {
    const [a, l] = await Promise.all([
      api<{ produkte: typeof artikel }>('/products').then(r => r.produkte).catch(() => []),
      api<typeof lieferanten>('/suppliers').catch(() => []),
    ]);
    setArtikel(a);
    setLieferanten(l);
  }, []);

  useEffect(() => {
    endeRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [zuege, denkt]);

  // Beim Verlassen der Seite: Mikrofon aus, Stimme aus. Sonst redet die App
  // weiter, während man längst woanders arbeitet.
  useEffect(() => () => {
    diktatStoppen.current?.();
    vorlesenStoppen();
  }, []);

  // --- Sprechen ------------------------------------------------------------
  function mikrofonUmschalten() {
    if (hoert) {
      diktatStoppen.current?.();
      diktatStoppen.current = null;
      setHoert(false);
      setZwischenstand('');
      return;
    }
    setFehler(null);
    const stopp = diktatStarten(sprache, {
      beiZwischenstand: setZwischenstand,
      beiErgebnis: (satz) => {
        setZwischenstand('');
        setEingabe(e => (e ? `${e} ${satz}` : satz));
      },
      beiFehler: (m) => { setFehler(m); setHoert(false); setZwischenstand(''); },
      beiEnde: () => { setHoert(false); setZwischenstand(''); diktatStoppen.current = null; },
    });
    if (!stopp) {
      setFehler('Dieser Browser kann kein Diktat. Chrome, Edge oder Safari können es.');
      return;
    }
    diktatStoppen.current = stopp;
    setHoert(true);
  }

  function vorlesenUmschalten(text: string) {
    if (liest) { vorlesenStoppen(); setLiest(false); return; }
    vorlesen(text, sprache);
    setLiest(true);
    // Die Sprachausgabe meldet ihr Ende nicht zuverlässig — nach einer groben
    // Schätzung wieder freigeben, damit der Knopf nicht hängen bleibt.
    setTimeout(() => setLiest(false), Math.min(60000, 400 + text.length * 60));
  }

  // --- Schreiben -----------------------------------------------------------
  async function fragen(e?: React.FormEvent) {
    e?.preventDefault();
    const text = eingabe.trim();
    if (!text || denkt) return;

    diktatStoppen.current?.();
    setHoert(false);
    setEingabe('');
    setFehler(null);
    setZuege(z => [...z, { rolle: 'ich', text, zeit: new Date().toISOString() }]);
    setDenkt(true);

    try {
      const antwort = await api<{ antwort: string; vorschlaege: Vorschlag[]; stand: Stand }>('/ki/chat', {
        methode: 'POST', daten: { text, sprache },
      });
      setZuege(z => [...z, {
        rolle: 'assistent', text: antwort.antwort,
        zeit: new Date().toISOString(), vorschlaege: antwort.vorschlaege,
      }]);
      setStand(antwort.stand);
    } catch (err) {
      setFehler(err instanceof ApiFehler ? err.message : 'Die Antwort kam nicht an.');
    } finally { setDenkt(false); }
  }

  // --- Dateien -------------------------------------------------------------
  async function dateienNehmen(liste: FileList | null) {
    if (!liste?.length) return;
    setLaedtHoch(true);
    setFehler(null);
    try {
      const neu: Anhang[] = [];
      for (const datei of Array.from(liste).slice(0, 5)) {
        neu.push(await dateiHochladen(datei));
      }
      setAnhaenge(a => [...a, ...neu].slice(0, 5));
    } catch (e) {
      setFehler(e instanceof Error ? e.message : 'Die Datei ging nicht durch.');
    } finally {
      setLaedtHoch(false);
      if (dateiFeld.current) dateiFeld.current.value = '';
      if (kameraFeld.current) kameraFeld.current.value = '';
    }
  }

  async function auswerten() {
    if (!anhaenge.length || scannt) return;
    setScannt(true);
    setFehler(null);
    try {
      await stammdatenLaden();
      const antwort = await api<{ vorgang: ScanVorgang; stand: Stand }>('/ki/scan', {
        methode: 'POST',
        daten: { dateiIds: anhaenge.map(a => a.id), hinweis: eingabe.trim() },
      });
      setStand(antwort.stand);
      setVorgang(antwort.vorgang);
      setAnhaenge([]);
      setEingabe('');
    } catch (e) {
      setFehler(e instanceof ApiFehler ? e.message : 'Der Beleg ließ sich nicht auswerten.');
    } finally { setScannt(false); }
  }

  // --- Vorschläge ----------------------------------------------------------
  async function vorschlagAusfuehren(zugIndex: number, v: Vorschlag) {
    try {
      await api('/ki/aktion', { methode: 'POST', daten: { typ: v.typ, daten: v.daten } });
      markieren(zugIndex, v.id, 'gebucht');
    } catch (e) {
      setFehler(e instanceof ApiFehler ? e.message : 'Das ließ sich nicht buchen.');
    }
  }

  function markieren(zugIndex: number, vorschlagId: string, was: 'gebucht' | 'verworfen') {
    setZuege(z => z.map((zug, i) => (i === zugIndex
      ? { ...zug, erledigt: { ...(zug.erledigt || {}), [vorschlagId]: was } }
      : zug)));
  }

  async function verlaufLoeschen() {
    try {
      await api('/ki/chat', { methode: 'DELETE' });
      setZuege([]);
    } catch { /* dann eben beim nächsten Mal */ }
  }

  // --- Anzeige -------------------------------------------------------------
  if (!flags?.ki) {
    return <TarifSperre was="Der KI-Assistent" upgradeAuf="professional" />;
  }

  if (laedt) {
    return <div className="flex h-64 items-center justify-center"><Spinner /></div>;
  }

  const frageRest = stand ? (stand.fragen.grenze < 0 ? '∞' : Math.max(0, stand.fragen.grenze - stand.fragen.verbraucht)) : '—';
  const scanRest = stand ? (stand.scans.grenze < 0 ? '∞' : Math.max(0, stand.scans.grenze - stand.scans.verbraucht)) : '—';

  return (
    <div className="mx-auto flex h-full max-w-3xl flex-col gap-4">

      {/* Kopf */}
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="flex items-center gap-3">
          <span className="flex h-10 w-10 items-center justify-center rounded-xl bg-marke-600 text-white">
            <Icon name="assistent" />
          </span>
          <div>
            <h1 className="font-semibold text-slate-900 dark:text-slate-100">{t('assistent.titel')}</h1>
            <p className="text-xs text-slate-500 dark:text-slate-400">
              {t('assistent.rest')}: {scanRest} {t('assistent.scans')} · {frageRest} {t('assistent.fragen')}
            </p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          {stand?.testmodus && <Abzeichen art="gelb">{t('assistent.testmodus')}</Abzeichen>}
          {zuege.length > 0 && (
            <Knopf variante="still" onClick={verlaufLoeschen}>{t('assistent.verlaufLoeschen')}</Knopf>
          )}
        </div>
      </div>

      {stand?.testmodus && (
        <Hinweis art="warnung" titel={t('assistent.testmodus')}>
          {t('assistent.testmodusText')}
        </Hinweis>
      )}

      {/* Gespräch */}
      <Karte klasse="flex min-h-0 flex-1 flex-col">
        <div className="flex-1 space-y-4 overflow-y-auto p-4">
          {!zuege.length && <Willkommen beiVorschlag={setEingabe} />}

          {zuege.map((zug, i) => (
            <div key={i} className={zug.rolle === 'ich' ? 'flex justify-end' : ''}>
              <div className={`max-w-[85%] ${zug.rolle === 'ich' ? '' : 'w-full'}`}>
                <div className={`rounded-2xl px-4 py-2.5 text-sm whitespace-pre-wrap ${
                  zug.rolle === 'ich'
                    ? 'rounded-br-sm bg-marke-600 text-white'
                    : 'rounded-bl-sm bg-slate-100 text-slate-800 dark:bg-slate-800 dark:text-slate-100'}`}>
                  {zug.text}
                </div>

                {zug.rolle === 'assistent' && kannVorlesen && zug.text && (
                  <button type="button" onClick={() => vorlesenUmschalten(zug.text)}
                          className="mt-1 text-xs text-slate-500 hover:underline dark:text-slate-400">
                    {liest ? t('assistent.stopp') : t('assistent.vorlesen')}
                  </button>
                )}

                {/* Vorschlagskarten */}
                {zug.vorschlaege?.map(v => {
                  const zustand = zug.erledigt?.[v.id];
                  return (
                    <div key={v.id}
                         className="mt-2 rounded-xl border border-marke-200 bg-marke-50 p-3 dark:border-marke-900 dark:bg-marke-950/30">
                      <p className="text-sm font-medium text-slate-900 dark:text-slate-100">{v.titel}</p>
                      <p className="mt-0.5 text-xs text-slate-600 dark:text-slate-300">{v.beschreibung}</p>
                      {zustand === 'gebucht' ? (
                        <p className="mt-2 flex items-center gap-1.5 text-xs font-medium text-emerald-700 dark:text-emerald-400">
                          <Icon name="haken" klasse="h-4 w-4" /> {t('assistent.gebucht')}
                        </p>
                      ) : zustand === 'verworfen' ? (
                        <p className="mt-2 text-xs text-slate-500 dark:text-slate-400">{t('assistent.verworfen')}</p>
                      ) : (
                        <div className="mt-2 flex gap-2">
                          <Knopf variante="primaer" onClick={() => vorschlagAusfuehren(i, v)}>
                            {t('assistent.bestaetigen')}
                          </Knopf>
                          <Knopf onClick={() => markieren(i, v.id, 'verworfen')}>
                            {t('aktion.abbrechen')}
                          </Knopf>
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>
          ))}

          {denkt && (
            <div className="flex items-center gap-2 text-sm text-slate-500 dark:text-slate-400">
              <Spinner klein /> {t('assistent.denkt')}
            </div>
          )}
          <div ref={endeRef} />
        </div>

        {/* Eingabe */}
        <div className="border-t border-slate-200 p-3 dark:border-slate-800">
          {fehler && <div className="mb-2"><Hinweis art="fehler">{fehler}</Hinweis></div>}

          {anhaenge.length > 0 && (
            <div className="mb-2 space-y-2">
              <div className="flex flex-wrap gap-2">
                {anhaenge.map(a => (
                  <span key={a.id}
                        className="flex items-center gap-2 rounded-lg border border-slate-200 bg-slate-50 px-2.5 py-1.5 text-xs dark:border-slate-700 dark:bg-slate-800">
                    <Icon name="anhang" klasse="h-3.5 w-3.5" />
                    <span className="max-w-[10rem] truncate">{a.name}</span>
                    <span className="text-slate-400">{groesseLesbar(a.groesse)}</span>
                    <button type="button" aria-label={t('allg.entfernen')}
                            onClick={() => setAnhaenge(l => l.filter(x => x.id !== a.id))}
                            className="text-slate-400 hover:text-red-600">×</button>
                  </span>
                ))}
              </div>
              <Knopf variante="primaer" onClick={auswerten} laedt={scannt} className="w-full">
                <Icon name="assistent" klasse="h-4 w-4" />
                {scannt ? t('assistent.wertetAus') : t('assistent.belegAuswerten')}
              </Knopf>
            </div>
          )}

          <form onSubmit={fragen} className="flex items-end gap-2">
            <input ref={dateiFeld} type="file" multiple accept="image/*,application/pdf" className="hidden"
                   onChange={e => dateienNehmen(e.target.files)} />
            {/* capture="environment" öffnet auf dem Handy direkt die Rückkamera. */}
            <input ref={kameraFeld} type="file" accept="image/*" capture="environment" className="hidden"
                   onChange={e => dateienNehmen(e.target.files)} />

            <button type="button" onClick={() => kameraFeld.current?.click()} disabled={laedtHoch}
                    aria-label={t('assistent.fotografieren')} title={t('assistent.fotografieren')}
                    className="rounded-lg p-2.5 text-slate-500 hover:bg-slate-100 disabled:opacity-50 dark:text-slate-400 dark:hover:bg-slate-800">
              <Icon name="kamera" />
            </button>
            <button type="button" onClick={() => dateiFeld.current?.click()} disabled={laedtHoch}
                    aria-label={t('assistent.datei')} title={t('assistent.datei')}
                    className="rounded-lg p-2.5 text-slate-500 hover:bg-slate-100 disabled:opacity-50 dark:text-slate-400 dark:hover:bg-slate-800">
              {laedtHoch ? <Spinner klein /> : <Icon name="anhang" />}
            </button>
            {kannSprechen && (
              <button type="button" onClick={mikrofonUmschalten}
                      aria-label={hoert ? t('assistent.hoertZu') : t('assistent.sprechen')}
                      title={hoert ? t('assistent.hoertZu') : t('assistent.sprechen')}
                      className={`rounded-lg p-2.5 transition ${hoert
                        ? 'animate-pulse bg-red-600 text-white'
                        : 'text-slate-500 hover:bg-slate-100 dark:text-slate-400 dark:hover:bg-slate-800'}`}>
                <Icon name="mikrofon" />
              </button>
            )}

            <textarea
              value={hoert && zwischenstand ? `${eingabe}${eingabe ? ' ' : ''}${zwischenstand}` : eingabe}
              onChange={e => setEingabe(e.target.value)}
              onKeyDown={e => {
                // Enter schickt, Umschalt+Enter macht einen Absatz.
                if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); void fragen(); }
              }}
              rows={1}
              placeholder={hoert ? t('assistent.hoertZu') : t('assistent.platzhalter')}
              className="max-h-32 min-h-[2.75rem] flex-1 resize-y rounded-lg border border-slate-300 bg-white px-3 py-2.5 text-sm text-slate-900 placeholder:text-slate-400 dark:border-slate-700 dark:bg-slate-950 dark:text-slate-100"
            />

            <Knopf type="submit" variante="primaer" disabled={!eingabe.trim() || denkt}
                   aria-label={t('assistent.senden')} className="h-11 px-3">
              <Icon name="senden" />
            </Knopf>
          </form>
        </div>
      </Karte>

      {vorgang && (
        <BelegPruefung
          vorgang={vorgang}
          artikel={artikel}
          lieferanten={lieferanten}
          darfBuchhaltung={!!flags?.accounting}
          beiAbbruch={() => setVorgang(null)}
          beiFertig={(zusammenfassung) => {
            setVorgang(null);
            setZuege(z => [...z, {
              rolle: 'assistent',
              text: `Beleg übernommen: ${zusammenfassung}.`,
              zeit: new Date().toISOString(),
            }]);
            void stammdatenLaden();
          }}
        />
      )}
    </div>
  );
}

/** Erste Ansicht: was der Assistent kann, zum Antippen. */
function Willkommen({ beiVorschlag }: { beiVorschlag: (text: string) => void }) {
  const { t } = useSprache();
  const beispiele = [
    t('assistent.beispiel1'),
    t('assistent.beispiel2'),
    t('assistent.beispiel3'),
    t('assistent.beispiel4'),
  ];
  return (
    <div className="py-6 text-center">
      <span className="mx-auto flex h-14 w-14 items-center justify-center rounded-2xl bg-marke-100 text-marke-700 dark:bg-marke-950 dark:text-marke-300">
        <Icon name="assistent" klasse="h-7 w-7" />
      </span>
      <p className="mt-3 font-medium text-slate-900 dark:text-slate-100">{t('assistent.willkommen')}</p>
      <p className="mx-auto mt-1 max-w-md text-sm text-slate-500 dark:text-slate-400">
        {t('assistent.willkommenText')}
      </p>
      {/* Ehrlich und knapp: Fotos und Fragen verlassen den Server. Wer das
          wissen will, erfährt es vor dem ersten Klick, nicht erst im
          Kleingedruckten. */}
      <p className="mx-auto mt-3 max-w-md text-xs text-slate-400 dark:text-slate-500">
        {t('assistent.datenschutz')}
      </p>
      <div className="mx-auto mt-4 grid max-w-md gap-2">
        {beispiele.map(b => (
          <button key={b} type="button" onClick={() => beiVorschlag(b)}
                  className="rounded-lg border border-slate-200 px-3 py-2 text-left text-sm text-slate-600 transition hover:border-marke-300 hover:bg-marke-50 dark:border-slate-700 dark:text-slate-300 dark:hover:border-marke-800 dark:hover:bg-marke-950/30">
            {b}
          </button>
        ))}
      </div>
    </div>
  );
}
